import { notification } from "antd";
import { useMutation, useQuery } from "react-query";

import {
  endpointCancelRetention,
  endpointCreateFiscalDoc,
  endpointGetTaxRetentionFile,
  endpointGetTaxRetentions,
} from "services/endpoints";

import { fnFetchData, fnFetchDataNewGateway } from "utils/requests";
import { removeFalseValueProperties } from "utils/general";

import { FISCAL_DOC_ISSUANCE_MODE } from "../constants/taxes";

import type { ItfSearchTaxRetentions } from "types/taxes";
import type { ItfResponseErrorHeru } from "types/commons";
import type { UseQueryResult, QueryObserverOptions, QueryKey } from "react-query";
import type { CancelFiscalDocParams, TypesToDownloadRetentionFile } from "types/taxes";

/* ---------------
--- QUERY KEYS ---
------------------*/
export const qkBaseTaxRetentions = (): QueryKey => ["tax-retentions"];

export const qkTaxRetentions = ({ filters }: { filters: ItfSearchTaxRetentions; }): QueryKey => {
  return [...qkBaseTaxRetentions(), { filters }];
};

export const qkTaxRetentionFile = (params: { docId: number; type?: TypesToDownloadRetentionFile }): QueryKey => {
  const { docId, type = "pdf" } = params;
  return [...qkBaseTaxRetentions(), "file", docId, type];
};

/* ------------------
--- DATA FETCHING ---
---------------------*/
export function useTaxRetentions(props?: UseTaxRetentionsProps): UseQueryResult<any, any> {
  const { options = {}, filters = { page: 1, limit: 50 } }: UseTaxRetentionsProps = props;
  const fts: ItfSearchTaxRetentions | any = removeFalseValueProperties({ obj: filters });
  const fn = () => fnFetchDataNewGateway({
    config: { url: endpointGetTaxRetentions({ filters: fts }) },
  });
  const rqOpts: QueryObserverOptions = { keepPreviousData: true };
  return useQuery(qkTaxRetentions({ filters: fts }), fn, Object.assign(rqOpts, options));
}

export function useTaxRetentionFile(props: UseTaxRetentionFileProps): UseQueryResult<any, any> {
  const { docId, type = "pdf", options = {} }: UseTaxRetentionFileProps = props;
  const fn = () => fnFetchData({ config: { url: endpointGetTaxRetentionFile({ docId, type }) } });
  return useQuery(qkTaxRetentionFile({ docId, type }), fn, options);
}

export function useCreateFiscalDoc({ options = {} }: { options?: QueryObserverOptions } = {}) {
  const fn = ({ taxpayerId, body }: CreateFiscalDocParams) =>
    fnFetchData({ config: { data: body, method: "POST", url: endpointCreateFiscalDoc({ taxpayerId }) } });
  const rqOpts = {
    onSuccess: (rData: any) =>
      notification.success({
        duration: 5,
        message: "Creación exitosa",
        description: `${FISCAL_DOC_ISSUANCE_MODE.tax_retention.description}. ID: ${rData.id})`,
      }),
    onError: (rError: ItfResponseErrorHeru) =>
      notification.error({
        duration: 5,
        message: "Se ha presentado un error",
        description: JSON.stringify(rError.message),
      }),
  };
  return useMutation(fn, Object.assign(rqOpts, options));
}

export function useCancelRetention({ options = {} }: { options?: QueryObserverOptions } = {}) {
  const fn = ({ docId }: CancelFiscalDocParams) =>
    fnFetchData({ config: { method: "POST", url: endpointCancelRetention({ docId }) } });
  return useMutation(fn, options);
}

/* ------------------
--- INTERNAL TYPES ---
---------------------*/
interface UseTaxRetentionsProps {
  filters: ItfSearchTaxRetentions;
  options?: QueryObserverOptions;
}

export interface UseTaxRetentionFileProps {
  docId: number;
  type?: TypesToDownloadRetentionFile;
  options?: QueryObserverOptions;
}

interface CreateFiscalDocParams {
  taxpayerId: number;
  body: any;
}
